/* eslint-disable no-console */

import { keccak_256 } from 'js-sha3';
import { useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { randomUUID } from 'lib/uuid';

const useStableUserId = () => {
  return useLocalStorage('randomSessionId', randomUUID())[0];
};

type SessionPath = [string, number];

const parseFlag = (flag: unknown): Array<SessionPath> => {
  try {
    if (
      Array.isArray(flag) &&
      flag.every(
        v =>
          Array.isArray(v) &&
          v.length === 2 &&
          typeof v[0] === 'string' &&
          typeof v[1] === 'number'
      )
    ) {
      return flag;
    }
  } catch {} // eslint-disable-line no-empty
  return [];
};

/** Byte is a number between 0 and 255 */
type Byte = number;

const matchesByte = (value: Byte, probability: number): boolean => {
  return value >= (256 * (100 - probability)) / 100;
};

const matchesPath = (
  uuid: string,
  path: SessionPath[0],
  probability: SessionPath[1]
): boolean => {
  const [byte] = keccak_256.digest(uuid + path);
  return matchesByte(byte, probability);
};

/**
 * Probabilistic session recording based on a feature flag
 * Example values for the flag below
 *
 * @example [["/football/play/classic/:fixture", 100]]
 * @example [["/fooball/market/*", 10]]
 */
export const useRecordPath = () => {
  const { pathname } = useLocation();

  const {
    flags: { usePathnameRecordSession },
  } = useFeatureFlags();

  const uuid = useStableUserId();
  const parsedFlag = parseFlag(usePathnameRecordSession);

  const matchesURLPath = parsedFlag.some(
    v => matchPath(v[0], pathname) && matchesPath(uuid, v[0], v[1])
  );
  const [matchedFlag, setMatchedFlag] = useState(matchesURLPath);

  // Stop recording when the flag is set to an empty string
  if (matchedFlag && parsedFlag.length === 0) {
    setMatchedFlag(false);
  }
  if (!matchedFlag && matchesURLPath) {
    setMatchedFlag(true);
  }

  return matchedFlag;
};
